import { Component, computed, effect, inject, Input, OnChanges, signal, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { BaseChartComponent, type EnergyType, type Interval } from './base-chart.component';
import { AverageEnergyResponse } from '../../../../api/models/average-energy-response';
import { AverageEnergyStatus } from '../../../../api/models/average-energy-status';
import { BaseChartDirective } from 'ng2-charts';
import { de } from 'date-fns/locale';
import { Chart, ChartDataset, ChartOptions } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { rxResource } from '@angular/core/rxjs-interop';
import { DateTimeValue } from '../../../../api/models/date-time-value';
import { SunResponse } from '../../../../api/models/sun-response';
import { LocationStateService } from '../../../../common/services/location-state.service';
import { environment } from '../../../../environments/environment';
import {getAverageEnergyStatus} from './average-energy-response.helper';

@Component({
  selector: 'app-grid-origin-chart',
  templateUrl: './chart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BaseChartDirective],
})
export class GridOriginChartComponent extends BaseChartComponent implements OnChanges {
  @Input() override interval: Interval = 'day';
  protected override get summaryType(): EnergyType { return 'grid'; }
  private _interval = signal<Interval>('day');

  @Input() set date(value: Date) {
    this._date.set(value);
  }

  get date(): Date {
    return this._date();
  }

  private _date = signal<Date>(new Date());
  @Input() data: AverageEnergyResponse | undefined;

  private readonly locationStateService = inject(LocationStateService);

  // Weather resource for sunrise/sunset data - automatically reloads when date changes
  weatherResource = rxResource<SunResponse, any>({
    params: computed(() => ({ body: { date: this._date().toISOString() } })),
    defaultValue: environment.showDefault
      ? {
        sunrise: this._date().toISOString(),
        sunset: this._date().toISOString(),
      }
      : { sunrise: undefined, sunset: undefined },
    stream: this.sunDataLoader,
  });

  lineChartOption: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      ...this.baseTooltipOptions,
      legend: {
        position: 'bottom',
        display: true,
        labels: {
          generateLabels: (chart: Chart) => {
            const root = document.documentElement;
            const productionColor = getComputedStyle(root).getPropertyValue('--color-production-yellow').trim();
            const consumptionColor = getComputedStyle(root).getPropertyValue('--color-consumption-blue').trim();
            const batteryColor = getComputedStyle(root).getPropertyValue('--color-battery-green').trim();
            const gridPositiveColor = this.getColor('gridPositive');
            const gridNegativeColor = this.getColor('gridNegative');
            return [
              { text: this.getLabel('consumption'), fillStyle: consumptionColor, strokeStyle: consumptionColor, datasetIndex: 0, hidden: chart.getDatasetMeta(0).hidden, index: 0 },
              { text: this.getLabel('batteryPositive'), fillStyle: batteryColor, strokeStyle: batteryColor, datasetIndex: 1, hidden: chart.getDatasetMeta(1).hidden, index: 1 },
              { text: this.getLabel('gridPositive'), fillStyle: 'rgba(0, 0, 0, 0)', strokeStyle: gridPositiveColor, lineWidth: this.getCapLineBorderWidth(), datasetIndex: 2, hidden: chart.getDatasetMeta(2).hidden, index: 2 },
              { text: this.getLabel('production'), fillStyle: productionColor, strokeStyle: productionColor, datasetIndex: 3, hidden: chart.getDatasetMeta(3).hidden, index: 3 },
              { text: this.getLabel('batteryNegative'), fillStyle: batteryColor, strokeStyle: batteryColor, datasetIndex: 4, hidden: chart.getDatasetMeta(4).hidden, index: 4 },
              { text: this.getLabel('gridNegative'), fillStyle: 'rgba(0, 0, 0, 0)', strokeStyle: gridNegativeColor, lineWidth: this.getCapLineBorderWidth(), datasetIndex: 5, hidden: chart.getDatasetMeta(5).hidden, index: 5 },
            ];
          },
        },
        onClick: (e, legendItem, legend) => {
          const chart = legend.chart;
          const item = legendItem as { datasetIndices?: number[]; datasetIndex?: number };
          const indices = item.datasetIndices ?? (item.datasetIndex !== undefined ? [item.datasetIndex] : []);
          const first = indices[0];
          if (first === undefined) return;
          const meta0 = chart.getDatasetMeta(first);
          const newHidden = !meta0.hidden;
          indices.forEach((i: number) => {
            chart.getDatasetMeta(i).hidden = newHidden;
          });
          chart.update();
        },
      },
      annotation: {
        annotations: {},
      },
    },
    scales: {
      y: {
        stacked: true,
        ticks: {
          stepSize: 1, // Display every 0.50 on the y-axis
          precision: 0, // Ensures ticks are displayed with two decimal places
          callback: (value, index, ticks) => this.getOriginAxisTickLabel(value, index, ticks),
        },
        beginAtZero: true,
        // Initial values; will be updated from LocationStateService.currentLocation
        min: -10,
        max: 10,
        border: {
          display: false
        }
      },
      x: {
        stacked: true,
        type: 'time',
        time: {
          unit: 'hour',
          tooltipFormat: 'HH:mm',
          displayFormats: {
            minute: 'HH:mm',
            hour: 'HH:mm',
          },
        },
        adapters: {
          date: {
            locale: de,
          },
        },
        border: {
          display: false
        }
      },
    },
  };

  private readonly sunriseSunsetEffect = effect(() => {
    const sun = this.weatherResource.value();
    const currentInterval = this._interval();

    if (currentInterval !== 'day') {
      this.removeAnnotations();
      return;
    }

    this.removeAnnotations();

    let updatedOptions = { ...this.lineChartOption };

    if (sun?.sunrise) {
      updatedOptions = this.addVerticalLine(updatedOptions, new Date(sun.sunrise), 'Sonnenaufgang', 'rgb(128,128,128)');
    }
    if (sun?.sunset) {
      updatedOptions = this.addVerticalLine(updatedOptions, new Date(sun.sunset), 'Sonnenuntergang', 'rgb(128,128,128)');
    }

    // Subtle marker for the current time (only shown when viewing today)
    updatedOptions = this.addCurrentTimeLine(updatedOptions, this._date());

    this.lineChartOption = updatedOptions;
    this.chartOptions.set(this.lineChartOption);

    if (this.chart) {
      this.chart.update();
    }
  });

  constructor() {
    super();
    Chart.register(annotationPlugin);

    // React to location changes and update chart bounds
    effect(() => {
      const range = this.locationStateService.currentLocation()?.displayChart?.gridOrigin;
      if (!range) {
        return;
      }

      const tickStep = this.getSymmetricAxisTickStep(
        Math.max(Math.abs(range.minkWh ?? 0), Math.abs(range.maxkWh ?? 0)),
      );
      this.lineChartOption = {
        ...this.lineChartOption,
        scales: {
          ...this.lineChartOption.scales,
          y: {
            ...(this.lineChartOption.scales?.['y'] ?? {}),
            min: range.minkWh ?? undefined,
            max: range.maxkWh ?? undefined,
            ticks: {
              ...((this.lineChartOption.scales?.['y'] as any)?.ticks ?? {}),
              stepSize: tickStep,
              precision: 0,
              callback: (value: string | number, index: number, ticks: any[]) => this.getOriginAxisTickLabel(value, index, ticks),
            },
          },
          x: this.lineChartOption.scales?.['x'],
        },
      };

      this.chartOptions.set(this.lineChartOption);
      if (this.chart) {
        this.chart.update();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('Grid origin chart received change', changes);
    if (changes['interval']) {
      this._interval.set(this.interval);
    }
    if (changes['data'] || changes['date'] || changes['interval']) {
      this.onResourceChange();
    }
  }

  private onResourceChange() {
    this.intializeChartData();
  }

  private intializeChartData() {
    console.log('initialize Grid origin Chart');
    if (!this.data) {
      return; // Exit if no data is available
    }

    const productionStatus = this.getStatus(getAverageEnergyStatus(this.data, 'production'));
    const completedProductionValues = this.processStatusValues(productionStatus);

    const consumptionPositiveStatus = this.getStatus(getAverageEnergyStatus(this.data, 'consumptionPositive') ?? getAverageEnergyStatus(this.data, 'consumption'));
    const completedConsumptionPositiveValues = this.processStatusValues(consumptionPositiveStatus);
    const consumptionStatus = this.getStatus(getAverageEnergyStatus(this.data, 'consumption') ?? getAverageEnergyStatus(this.data, 'consumptionPositive'));
    const completedConsumptionValues = this.processStatusValues(consumptionStatus);
    const completedProductionSurplusValues = this.calculateProductionSurplus(
      completedProductionValues,
      completedConsumptionValues,
    );

    const gridPositiveStatus = this.getStatus(getAverageEnergyStatus(this.data, 'gridPositive'));
    const completedGridPositiveValues = this.processStatusValues(gridPositiveStatus);
    const gridNegativeStatus = this.getStatus(getAverageEnergyStatus(this.data, 'gridNegative'));
    const completedGridNegativeValues = this.processStatusValues(gridNegativeStatus);

    const batteryPositiveStatus = this.getStatus(getAverageEnergyStatus(this.data, 'batteryPowerPositive'));
    const completedBatteryPositiveValues = this.processStatusValues(batteryPositiveStatus);

    const batteryNegativeStatus = this.getStatus(getAverageEnergyStatus(this.data, 'batteryPowerNegative'));
    const completedBatteryNegativeValues = this.processStatusValues(batteryNegativeStatus);

    // POSITIVE SIDE:
    // Stack: consumptionPositive + batteryPositive
    // Cap:   gridPositive
    const positiveCapped = this.capPositiveToGrid(
      completedConsumptionPositiveValues,
      completedBatteryPositiveValues,
      completedGridPositiveValues,
    );

    // Distinct legend labels for positive stack.
    const consumptionPositiveDataset = this.createDataset(
      this.getLabel('consumption'),
      positiveCapped.consumption,
      'consumption',
      true,
    );
    const batteryPositiveDataset = this.createDataset(
      this.getLabel('batteryPositive'),
      positiveCapped.batteryPositive,
      'battery',
      true,
    );
    const gridPositiveDataset = this.createGridDataset(completedGridPositiveValues, 'gridPositive');

    // NEGATIVE SIDE:
    // Stack: production surplus (production - consumption, shown as negative) + batteryNegative
    // Cap:   gridNegative
    const negativeCapped = this.capNegativeToGrid(
      completedProductionSurplusValues,
      completedBatteryNegativeValues,
      completedGridNegativeValues,
    );

    const productionNegativeDataset = this.createDataset(
      this.getLabel('production'),
      negativeCapped.production,
      'production',
      true,
    );
    const batteryNegativeDataset = this.createDataset(
      this.getLabel('batteryNegative'),
      negativeCapped.batteryNegative,
      'battery',
      true,
    );
    const gridNegativeDataset = this.createGridDataset(negativeCapped.gridNegative, 'gridNegative');

    // Add labels only once (based on production values)
    const labels: Date[] = this.populateLabels(completedProductionValues);
    const datasets = [];
    // Order matters for the custom plugin (first 3 datasets).
    // Positive stack (above zero).
    datasets.push(consumptionPositiveDataset);
    datasets.push(batteryPositiveDataset);
    datasets.push(gridPositiveDataset);
    // Negative stack (below zero).
    datasets.push(productionNegativeDataset);
    datasets.push(batteryNegativeDataset);
    datasets.push(gridNegativeDataset);

    // Update chart data
    this.updateChart(labels, datasets);
  }

  /**
   * Extracts the status object or provides a default.
   */
  private getStatus(status: AverageEnergyStatus | undefined): AverageEnergyStatus {
    return status ?? { deviceNames: {}, deviceTimeValues: {}, timeBucket: '' };
  }

  /**
   * Processes device time values by summarizing and completing them.
   */
  private processStatusValues(status: AverageEnergyStatus): DateTimeValue[] {
    const summarizedValues = super.summarizeByTimestamp(Object.values(status.deviceTimeValues || {}));
    return super.completeData(summarizedValues, 'day', this.date, 'zero', status.timeBucket);
  }

  /**
   * Compute production-minus-consumption per timestamp for the negative stack:
   * first negative source = max(production - consumption, 0)
   */
  private calculateProductionSurplus(
    productionValues: DateTimeValue[],
    consumptionValues: DateTimeValue[],
  ): DateTimeValue[] {
    const consumptionByRead = new Map<string, number>();
    consumptionValues.forEach((v) => {
      if (!v.read) return;
      consumptionByRead.set(v.read, Math.max(0, v.value ?? 0));
    });

    return productionValues.map((v) => {
      const production = Math.max(0, v.value ?? 0);
      const consumption = v.read ? (consumptionByRead.get(v.read) ?? 0) : 0;
      return {
        ...v,
        value: Math.max(0, production - consumption),
      };
    });
  }

  /**
   * POSITIVE SIDE CAP:
   * Caps/scales down (positive) consumption + (positive) battery per point
   * so the stacked sum never exceeds (positive) grid.
   * This keeps the relative proportions between the 2 stacked sources.
   */
  private capPositiveToGrid(
    consumption: DateTimeValue[],
    batteryPositive: DateTimeValue[],
    gridPositive: DateTimeValue[],
  ): { consumption: DateTimeValue[]; batteryPositive: DateTimeValue[] } {
    // Work on copies to avoid unexpected side-effects.
    const p = consumption.map((v) => ({ ...v, value: Math.max(0, v.value ?? 0) }));
    const bPos = batteryPositive.map((v) => ({ ...v, value: Math.max(0, v.value ?? 0) }));
    const gPos = gridPositive.map((v) => ({ ...v, value: Math.max(0, v.value ?? 0) }));

    const idxByRead = (arr: DateTimeValue[]) => {
      const m = new Map<string, number>();
      arr.forEach((v, i) => {
        if (v.read) m.set(v.read, i);
      });
      return m;
    };

    const pIdx = idxByRead(p);
    const bIdx = idxByRead(bPos);
    const gIdx = idxByRead(gPos);

    // Iterate gridPositive timestamps (the "cap") and scale down the corresponding stacked values.
    gPos.forEach((gv) => {
      if (!gv.read) return;
      const gridVal = Math.max(0, gv.value ?? 0);

      const pi = pIdx.get(gv.read);
      const bi = bIdx.get(gv.read);

      const pv = pi !== undefined ? (p[pi].value ?? 0) : 0;
      const bv = bi !== undefined ? (bPos[bi].value ?? 0) : 0;
      const total = pv + bv;

      if (total <= gridVal || total <= 0) return;

      const factor = gridVal / total;
      if (pi !== undefined) p[pi].value = pv * factor;
      if (bi !== undefined) bPos[bi].value = bv * factor;
    });

    return { consumption: p, batteryPositive: bPos };
  }

  /**
   * NEGATIVE SIDE CAP (Grid as cap):
   * Caps production + battery export per point so the stacked negative sum
   * never exceeds the (negative) grid export.
   *
   * Allocation is **priority-based** per timestamp:
   *  1. Use as much production as possible (up to the grid export magnitude).
   *  2. Then use battery export with whatever grid capacity is left.
   *
   * This means we **do not scale proportionally** anymore – grid export is
   * filled with production first, then battery.
   *
   * Inputs:
   *  - production: production surplus values (kWh), already reduced by consumption
   *  - batteryNegative: negative values (battery discharge)
   *  - gridNegative: negative values (export to grid)
   *
   * Output values are NEGATIVE (for rendering below zero).
   */
  private capNegativeToGrid(
    production: DateTimeValue[],
    batteryNegative: DateTimeValue[],
    gridNegative: DateTimeValue[],
  ): {
    production: DateTimeValue[];
    batteryNegative: DateTimeValue[];
    gridNegative: DateTimeValue[];
  } {
    // Work internally with magnitudes (all >= 0).
    const prodMag = production.map((v) => ({
      ...v,
      value: Math.max(0, v.value ?? 0),
    }));
    const battMag = batteryNegative.map((v) => ({
      ...v,
      value: Math.max(0, Math.abs(v.value ?? 0)),
    }));
    const gridMag = gridNegative.map((v) => ({
      ...v,
      value: Math.max(0, Math.abs(v.value ?? 0)),
    }));

    const idxByRead = (arr: DateTimeValue[]) => {
      const m = new Map<string, number>();
      arr.forEach((v, i) => {
        if (v.read) m.set(v.read, i);
      });
      return m;
    };

    const pIdx = idxByRead(prodMag);
    const bIdx = idxByRead(battMag);

    // Iterate grid export magnitudes (the cap) and allocate with priority.
    gridMag.forEach((gv) => {
      if (!gv.read) return;
      const gridCap = Math.max(0, gv.value ?? 0);

      const pi = pIdx.get(gv.read);
      const bi = bIdx.get(gv.read);

      const pv = pi !== undefined ? (prodMag[pi].value ?? 0) : 0;
      const bv = bi !== undefined ? (battMag[bi].value ?? 0) : 0;

      if (gridCap <= 0 || (pv <= 0 && bv <= 0)) {
        // No export possible at this timestamp – zero both.
        if (pi !== undefined) prodMag[pi].value = 0;
        if (bi !== undefined) battMag[bi].value = 0;
        return;
      }

      // 1) Allocate production first.
      const useProd = Math.min(pv, gridCap);
      const remaining = Math.max(0, gridCap - useProd);

      // 2) Allocate battery export from what remains.
      const useBatt = Math.min(bv, remaining);

      if (pi !== undefined) prodMag[pi].value = useProd;
      if (bi !== undefined) battMag[bi].value = useBatt;
    });

    // Convert magnitudes back to negative values for rendering below zero.
    const productionOut = prodMag.map((v) => ({ ...v, value: -(v.value ?? 0) }));
    const batteryNegativeOut = battMag.map((v) => ({ ...v, value: -(v.value ?? 0) }));
    const gridNegativeOut = gridMag.map((v) => ({ ...v, value: -(v.value ?? 0) }));

    return {
      production: productionOut,
      batteryNegative: batteryNegativeOut,
      gridNegative: gridNegativeOut,
    };
  }


  /**
   * Populates labels based on processed values.
   */
  private populateLabels(values: DateTimeValue[]): Date[] {
    const labels: Date[] = [];
    values.forEach((item) => {
      const date = new Date(item.read ?? '');
      if (!isNaN(date.getTime())) {
        labels.push(date);
      }
    });
    return labels;
  }

  /**
   * Creates a dataset for the chart.
   */
  private createDataset(label: string, values: DateTimeValue[], colorKey: string, stacked: boolean): ChartDataset {
    const color = this.getColor(colorKey);
    return {
      label,
      data: values.map((v) => v.value!),
      borderColor: color,
      backgroundColor: stacked ? color : 'transparent',
      fill: stacked ? true : 'end',
      pointRadius: 0,
      pointHoverRadius: 0,
      tension: 0.1,
      cubicInterpolationMode: 'default',
      stack: stacked ? 'stack1' : undefined,
      borderWidth: this.isMobile() ? 1 : 2,
    };
  }

  /**
   * Creates the grid dataset: color is taken from getColor (legend is shown via custom 4-label legend).
   */
  private createGridDataset(values: DateTimeValue[], colorKey: string): ChartDataset {
    const color = this.getColor(colorKey);
    const dataset: ChartDataset & { isCapLine: boolean } = {
      label: this.getLabel(colorKey),
      data: values.map((v) => v.value!),
      borderColor: color,
      backgroundColor: 'transparent',
      fill: false,
      pointRadius: 0,
      pointHoverRadius: 0,
      tension: 0.1,
      cubicInterpolationMode: 'default',
      borderWidth: this.getCapLineBorderWidth(),
      isCapLine: true,
      order: 99,
    };
    return dataset;
  }

  /**
   * Updates the chart with the provided labels and datasets.
   */
  private updateChart(labels: Date[], datasets: Record<string, any>[]): void {
    this.chartType.set('line');
    // Re-apply the current-time marker on every rebuild so it survives view switches
    if (this._interval() === 'day') {
      this.lineChartOption = this.addCurrentTimeLine(this.lineChartOption, this._date());
    }
    this.chartOptions.set(this.lineChartOption);
    this.chartData.set({
      labels: labels,
      datasets: datasets,
    });
    if (this.chart) {
      this.chart.update();
    }
  }

  private removeAnnotations() {
    if (this.lineChartOption.plugins?.annotation) {
      this.lineChartOption.plugins.annotation.annotations = {};
      this.lineChartOption = { ...this.lineChartOption };
    }

    if (this.chart?.options?.plugins?.annotation) {
      this.chart.options.plugins.annotation.annotations = {};
    }

    this.chartOptions.set(this.lineChartOption);

    if (this.chart) {
      this.chart.update();
    }
  }

  plugin = {
    id: 'cutterPlugin',
    afterDatasetsDraw(chart: any) {
      const {
        ctx,
        chartArea: { top, bottom },
        scales: { x, y },
      } = chart;
      const baseDataset = chart.data.datasets[0];
      const topDataset = chart.data.datasets[1];
      const cutDataset = chart.data.datasets[2];

      chart.data.labels.forEach((label: any) => {
        const i = chart.data.labels.indexOf(label);
        const base = baseDataset.data[i];
        const topVal = topDataset.data[i];
        const stackTotal = base + topVal;
        const cutVal = cutDataset.data[i];

        if (cutVal >= stackTotal) {
          // draw nothing — entire stack is under the cutter
          return;
        }

        if (cutVal <= 0) return; // nothing to cut

        const barWidth = x.getPixelForTick(i + 1) - x.getPixelForTick(i);
        const centerX = x.getPixelForValue(i);
        const barLeft = centerX - barWidth / 4;
        const barRight = centerX + barWidth / 4;

        const yCut = y.getPixelForValue(cutVal);
        const yTop = y.getPixelForValue(stackTotal);

        ctx.save();
        ctx.fillStyle = 'white';
        ctx.fillRect(barLeft, yTop, barRight - barLeft, yCut - yTop);
        ctx.restore();
      });
    },
  };
}
